import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Bot, Link2, AlertCircle, ExternalLink, Undo2, Mail } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const LABEL_COLORS = { promising: "#008A00", duplicate: "#0050FF", off_topic: "#D32F2F" };
const fmt = (iso) => (iso || "").slice(0, 19).replace("T", " ");

export default function AdminAgentRun() {
  const { id } = useParams();
  const [run, setRun] = useState(null);
  const [error, setError] = useState(null);

  const load = () => api.get(`/admin/agent/runs/${id}`)
    .then((r) => setRun(r.data))
    .catch((err) => setError(err?.response?.data?.detail || "Could not load run"));

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied (admins only)");
    } catch {
      toast.error("Could not copy link");
    }
  };

  if (error) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-20" data-testid="agent-run-error">
        <BackLink />
        <div className="mt-6 border border-[#D32F2F] bg-[#FFEBEE] text-[#D32F2F] p-4 font-mono-plex text-sm">{error}</div>
      </div>
    );
  }
  if (!run) return <div className="max-w-5xl mx-auto px-6 py-20 font-mono-plex text-sm uppercase tracking-wider text-[#555]">Loading…</div>;

  const states = run.entry_states || {};
  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="agent-run-page">
      <BackLink />
      <RunHeader run={run} onCopy={copyLink} />
      {run.error && (
        <div className="mt-6 border border-[#D32F2F] bg-[#FFEBEE] text-[#D32F2F] p-3 font-mono-plex text-xs flex gap-2" data-testid="agent-run-error-msg">
          <AlertCircle className="w-4 h-4 shrink-0" /> {run.error}
        </div>
      )}
      {run.discover && <DraftsSection d={run.discover} dry={run.dry_run} states={states} />}
      {!run.dry_run && !run.test_run && <DigestPanel run={run} onSent={load} />}
      {run.review?.ready_to_publish?.length > 0 && <ReadySection rows={run.review.ready_to_publish} states={states} />}
      {run.review && <PublishedSection runId={run.id} r={run.review} states={states} onUndone={load} />}
      {run.tips && <TipsSection t={run.tips} states={states} />}
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/admin/agent" className="inline-flex items-center gap-2 font-mono-plex text-sm uppercase tracking-wider hover:underline" data-testid="back-to-agent">
      <ArrowLeft className="w-4 h-4" /> Back to AI agent
    </Link>
  );
}

function duration(run) {
  if (!run.started_at || !run.finished_at) return "—";
  const s = Math.round((new Date(run.finished_at) - new Date(run.started_at)) / 1000);
  return s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`;
}

function RunHeader({ run, onCopy }) {
  const trigger = run.triggered_by === "schedule" ? "Daily schedule" : run.triggered_by || "Manual (unknown)";
  return (
    <div className="mt-4 border-b border-black/15 pb-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Bot className="w-7 h-7" />
          <div>
            <div className="overline">/agent-run</div>
            <h1 className="font-display text-4xl font-black tracking-tighter mt-1" data-testid="agent-run-title">Run {run.id?.slice(0, 8)}</h1>
          </div>
        </div>
        <Button onClick={onCopy} variant="outline" className="rounded-none border-black h-10" data-testid="agent-run-copy-link">
          <Link2 className="w-4 h-4 mr-2" /> Copy link
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-black/15 border border-black/15 mt-6">
        <Meta label="Started" value={`${fmt(run.started_at)} UTC`} testid="agent-run-started" />
        <Meta label="Duration" value={duration(run)} testid="agent-run-duration" />
        <Meta label="Status" value={run.dry_run ? `${run.status} · dry run` : run.status} testid="agent-run-status" />
        <Meta label="Phases" value={(run.phases_requested || []).join(", ")} testid="agent-run-phases" />
        <div className="col-span-2 md:col-span-1 bg-white"><Meta label="Triggered by" value={trigger} testid="agent-run-trigger" /></div>
      </div>
    </div>
  );
}

function Meta({ label, value, testid }) {
  return (
    <div className="bg-white p-4 min-w-0">
      <div className="font-mono-plex text-[11px] uppercase tracking-wider text-[#555]">{label}</div>
      <div className="text-sm font-medium mt-1 break-words" data-testid={testid}>{value || "—"}</div>
    </div>
  );
}

function Section({ title, summary, testid, children, empty }) {
  return (
    <section className="mt-10" data-testid={testid}>
      <div className="flex items-baseline justify-between gap-3 flex-wrap border-b border-black pb-2">
        <h2 className="font-display text-2xl font-black">{title}</h2>
        <span className="font-mono-plex text-xs uppercase tracking-wider text-[#555]">{summary}</span>
      </div>
      {empty ? <p className="text-sm text-[#555] border border-dashed border-black/20 p-5 mt-3">{empty}</p> : <div className="divide-y divide-black/10 border-x border-b border-black/15 bg-white">{children}</div>}
    </section>
  );
}

function StateTag({ state }) {
  if (!state) return null;
  const cls = state === "deleted" ? "border-[#D32F2F] text-[#D32F2F]" : "border-black/40";
  return <span className={`self-start font-mono-plex text-[10px] uppercase tracking-wider border px-1.5 py-0.5 whitespace-nowrap ${cls}`} data-testid="agent-run-state-tag">now: {state.replace("_", " ")}</span>;
}

function DraftsSection({ d, dry, states }) {
  const drafts = d.drafts || [];
  return (
    <Section
      title={dry ? "Would draft" : "Drafts created"}
      summary={`${d.candidates ?? 0} candidates · ${d.unseen ?? 0} new · ${d.drafted ?? 0} ${dry ? "would draft" : "drafted"} · ${d.skipped ?? 0} skipped`}
      testid="agent-run-drafts"
      empty={drafts.length === 0 && "No drafts in this run."}
    >
      {drafts.map((x, i) => (
        <div key={x.id || x.url || i} className="p-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3" data-testid={`agent-run-draft-${i}`}>
          <div className="min-w-0 flex-1">
            {x.id && states[x.id] !== "deleted" ? <Link to={`/admin/edit/${x.id}`} className="font-display font-bold underline break-words">{x.title}</Link> : <div className="font-display font-bold break-words">{x.title}</div>}
            {x.url && (
              <a href={x.url} target="_blank" rel="noopener noreferrer" className="mt-1 flex items-center gap-1 font-mono-plex text-[11px] text-[#555] break-all hover:underline">
                {x.url} <ExternalLink className="w-3 h-3 shrink-0" />
              </a>
            )}
            {x.reason && <div className="text-xs text-[#555] mt-1">Citation check: {x.verified ? "verified" : "unverified"} ({x.reason})</div>}
          </div>
          <StateTag state={states[x.id]} />
        </div>
      ))}
    </Section>
  );
}

function PublishedSection({ runId, r, states, onUndone }) {
  const pub = r.published || [];
  const undone = Object.fromEntries((r.undone || []).map((u) => [u.id, u]));
  const [target, setTarget] = useState(null);
  return (
    <Section
      title="Auto-published"
      summary={`scanned ${r.scanned ?? 0} · ${r.citations_verified ?? 0} citation(s) verified · ${pub.length} published`}
      testid="agent-run-published"
      empty={pub.length === 0 && "Nothing was published in this run."}
    >
      {pub.map((x, i) => {
        const u = undone[x.id];
        const canUndo = !u && states[x.id] === "published";
        return (
          <div key={x.id || i} className="p-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3" data-testid={`agent-run-published-${i}`}>
            <div className="min-w-0 flex-1">
              {states[x.id] === "deleted"
                ? <div className="font-display font-bold break-words">{x.title}</div>
                : <Link to={u ? `/admin/edit/${x.id}` : `/entry/${x.id}`} className="font-display font-bold underline break-words">{x.title}</Link>}
              <div className="text-xs text-[#555] mt-1">{x.verified_citations} verified citations</div>
              {u && (
                <div className="text-xs mt-2 border-l-2 border-[#D32F2F] pl-2" data-testid={`agent-run-undone-${i}`}>
                  Unpublished by {u.by} · {fmt(u.at)} UTC — "{u.reason}"
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 self-start">
              <StateTag state={states[x.id]} />
              {canUndo && (
                <Button variant="outline" onClick={() => setTarget(x)} className="rounded-none border-[#D32F2F] text-[#D32F2F] h-8 px-3 text-xs" data-testid={`agent-run-undo-${i}`}>
                  <Undo2 className="w-3.5 h-3.5 mr-1" /> Unpublish
                </Button>
              )}
            </div>
          </div>
        );
      })}
      <UndoDialog runId={runId} entry={target} onClose={() => setTarget(null)} onDone={() => { setTarget(null); onUndone(); }} />
    </Section>
  );
}

function ReadySection({ rows, states }) {
  return (
    <Section title="Ready to publish" summary={`${rows.length} flagged · auto-publish was paused`} testid="agent-run-ready">
      {rows.map((x, i) => (
        <div key={x.id || i} className="p-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3" data-testid={`agent-run-ready-${i}`}>
          <div className="min-w-0 flex-1">
            {states[x.id] === "deleted"
              ? <div className="font-display font-bold break-words">{x.title}</div>
              : <Link to={`/admin/edit/${x.id}`} className="font-display font-bold underline break-words">{x.title}</Link>}
            <div className="text-xs text-[#555] mt-1">{x.verified_citations} verified citations · a human editor must publish</div>
          </div>
          <StateTag state={states[x.id]} />
        </div>
      ))}
    </Section>
  );
}

function UndoDialog({ runId, entry, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setReason(""); }, [entry]);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/agent/runs/${runId}/unpublish`, { entry_id: entry.id, reason: reason.trim() });
      toast.success("Unpublished — sent back to In review");
      onDone();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not unpublish");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!entry} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none" data-testid="agent-undo-dialog">
        <DialogHeader>
          <DialogTitle>Unpublish this entry?</DialogTitle>
          <DialogDescription>
            "{entry?.title}" will be removed from the public site and sent back to In review, so a human editor must re-approve it. Your reason is saved in the audit log.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why are you unpublishing? (at least 5 characters)"
          className="rounded-none border-black min-h-24"
          maxLength={500}
          data-testid="agent-undo-reason"
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-black" data-testid="agent-undo-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 5} className="rounded-none bg-[#D32F2F] hover:bg-[#B71C1C] text-white" data-testid="agent-undo-confirm">
            {busy ? "Unpublishing…" : "Unpublish"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DigestPanel({ run, onSent }) {
  const [busy, setBusy] = useState(false);
  const d = run.digest;
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/agent/runs/${run.id}/digest`, null, { params: { dry_run: false } });
      if (r.data.skipped) toast.error(`Not sent: ${r.data.skipped}`);
      else toast.success(`Digest sent to ${r.data.sent} admin(s)${r.data.failed?.length ? `, ${r.data.failed.length} failed` : ""}`);
      onSent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send digest");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-6 flex items-center justify-between gap-3 flex-wrap border border-black/15 bg-white p-4" data-testid="agent-run-digest">
      <div className="flex items-center gap-2 text-sm min-w-0">
        <Mail className="w-4 h-4 shrink-0" />
        <span className="break-words" data-testid="agent-run-digest-status">
          {d ? `Digest emailed to ${d.sent} admin(s) · ${fmt(d.at)} UTC${d.failed?.length ? ` · ${d.failed.length} failed` : ""}` : "No digest email sent for this run yet."}
        </span>
      </div>
      <Button variant="outline" onClick={send} disabled={busy} className="rounded-none border-black h-9" data-testid="agent-run-digest-send">
        {busy ? "Sending…" : d ? "Resend digest" : "Email digest to admins"}
      </Button>
    </div>
  );
}

function TipsSection({ t, states }) {
  const decisions = t.decisions || [];
  const promoted = Object.fromEntries((t.promoted || []).map((p) => [p.tip_id, p]));
  return (
    <Section
      title="Tip decisions"
      summary={`scored ${t.scored ?? 0} · promising ${t.labels?.promising || 0} · duplicate ${t.labels?.duplicate || 0} · off-topic ${t.labels?.off_topic || 0} · ${t.promoted?.length || 0} promoted`}
      testid="agent-run-tips"
      empty={decisions.length === 0 && (t.scored ? "Per-tip decisions weren't recorded for this run." : "No tips were scored in this run.")}
    >
      {decisions.map((x, i) => {
        const p = promoted[x.tip_id];
        return (
          <div key={x.tip_id || i} className="p-4 border-l-4" style={{ borderLeftColor: LABEL_COLORS[x.label] || "#000" }} data-testid={`agent-run-tip-${i}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="font-display font-bold break-words min-w-0 flex-1">{x.title}</div>
              <span className="font-mono-plex text-xs uppercase tracking-wider whitespace-nowrap" style={{ color: LABEL_COLORS[x.label] }}>
                {(x.label || "").replace("_", "-")}{x.confidence != null && ` · ${Math.round(x.confidence * 100)}%`}
              </span>
            </div>
            {x.reason && <p className="text-sm text-[#333] mt-1 break-words">{x.reason}</p>}
            {p && (
              <div className="mt-2 flex items-center gap-2 flex-wrap text-xs">
                {states[p.entry_id] === "deleted"
                  ? <span data-testid={`agent-run-tip-promoted-${i}`}>Promoted to draft (since deleted)</span>
                  : <Link to={`/admin/edit/${p.entry_id}`} className="underline" data-testid={`agent-run-tip-promoted-${i}`}>Promoted to draft →</Link>}
                <StateTag state={states[p.entry_id]} />
              </div>
            )}
          </div>
        );
      })}
    </Section>
  );
}
